import { logger } from '@/lib/logger';
import {
  ChevronDown,
  Users,
  Wallet,
  Copy,
  Check,
  Gavel,
  Handbag,
  Book,
  Sprout,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { walletService } from '@/services/wallet.service';
import { useGetUser, useAuth } from '@/hooks/useAuth';
import { useLocation, useNavigate, NavLink } from 'react-router';
import { useConnection } from 'wagmi';
import { useEffect, useState } from 'react';
import { asetquLandEnabled } from '@/lib/features';

// Helper function to format balance
const formatBalance = (balance: string): string => {
  const num = parseFloat(balance);

  if (isNaN(num)) return '0.00';

  // For numbers >= 1 billion
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2) + 'B';
  }
  // For numbers >= 1 million
  if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2) + 'M';
  }
  // For numbers >= 10 thousand
  if (num >= 10_000) {
    return (num / 1_000).toFixed(2) + 'K';
  }
  // For regular numbers, show with 2 decimals
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const TopNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { data: user, isLoading } = useGetUser();
  const { address } = useConnection();
  const { logout } = useAuth();
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const [copied, setCopied] = useState(false);

  // Fetch user balance
  const { data: balances } = useQuery({
    queryKey: ['user-balances'],
    queryFn: () => walletService.getUserBalances(),
    staleTime: 1000 * 30, // Cache for 30 seconds
    enabled: !!user, // Only fetch when authenticated
  });

  // Close wallet menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setShowWalletMenu(false);
    if (showWalletMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showWalletMenu]);

  const handleCopyAddress = async () => {
    if (address) {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDisconnect = async () => {
    try {
      setShowWalletMenu(false);
      await logout.mutateAsync();
      navigate('/login');
    } catch (error) {
      logger.error('Logout error:', error);
      // Force redirect even if logout fails
      navigate('/login');
    }
  };

  // Don't show nav on login page or if not authenticated
  if (pathname === '/login' || isLoading || !user) {
    return null;
  }

  const navItems = [
    { label: 'HOME', path: '/' },
    { label: 'PROPRIBID', path: '/propribid' },
    { label: 'PROPRIMARKET', path: '/proprimarket' },
    { label: 'PROPRIVOUCHER', path: '/proprivoucher' },
    ...(asetquLandEnabled ? [{ label: 'AsetQu LAND', path: '/asetqu-land' }] : []),
    { label: 'ASET ACADEMY', path: '/academy' },
  ];

  return (
    <nav className='w-full bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm'>
      <div className='max-w-full mx-auto px-8 h-16 flex items-center justify-between'>
        {/* Left: Logo + Brand */}
        <div className='flex items-center gap-3'>
          <img src='/logo2.png' alt='AsetQu' className='w-10 h-10 object-contain' />
          <div className='flex flex-col'>
            <span className='text-blue-900 font-extrabold text-lg tracking-tight leading-none'>
              AsetQu <span className='font-medium text-gray-500'>HUB</span>
            </span>
          </div>
        </div>

        {/* Center: Navigation Links */}
        <div className='flex items-center gap-1'>
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) =>
                `px-4 py-2 text-sm transition-all relative ${
                  isActive
                    ? 'text-blue-600 font-bold'
                    : 'font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {item.label}
                  {isActive && (
                    <div className='absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 rounded-t-full' />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* Right: Token Balance + User Menu */}
        <div className='flex items-center gap-3'>
          {/* Token Balance */}
          <div
            className='flex items-center gap-2 bg-gradient-to-r from-blue-50 to-cyan-50 px-4 py-2 rounded-full border border-blue-100 shadow-sm group relative cursor-help'
            title={
              balances?.tokenBalance.balanceFormatted
                ? `${parseFloat(balances.tokenBalance.balanceFormatted).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })} AsetQu`
                : '0.00 AsetQu'
            }
          >
            <img src='/asetqu.png' alt='Logo' className='w-5 h-5 object-contain shrink-0' />
            <span className='text-sm font-bold text-gray-800'>
              $
              {balances?.tokenBalance.balanceFormatted
                ? formatBalance(balances.tokenBalance.balanceFormatted)
                : '0.00'}
            </span>

            {/* Tooltip */}
            {balances?.tokenBalance.balanceFormatted && (
              <div className='absolute top-full right-0 mt-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50'>
                <p className='font-mono'>
                  {parseFloat(balances.tokenBalance.balanceFormatted).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 8,
                  })}{' '}
                  AsetQu
                </p>
                <div className='absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45'></div>
              </div>
            )}
          </div>

          {/* User/Wallet Dropdown */}
          {address && (
            <div className='relative'>
              <button
                onClick={e => {
                  e.stopPropagation();
                  setShowWalletMenu(!showWalletMenu);
                }}
                className='flex items-center gap-2 bg-gradient-to-r from-orange-400 to-pink-500 hover:opacity-90 px-4 py-2 rounded-full shadow-sm transition-all'
              >
                <div className='w-6 h-6 rounded-full bg-white/20 flex items-center justify-center'>
                  <Wallet size={14} className='text-white' />
                </div>
                <ChevronDown
                  size={14}
                  className={`text-white transition-transform ${showWalletMenu ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown Menu */}
              {showWalletMenu && (
                <div className='absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-50'>
                  <div className='p-4 bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-gray-100'>
                    <div className='flex items-center justify-between mb-1'>
                      <p className='text-xs text-gray-600'>Connected Wallet</p>
                      <button
                        onClick={handleCopyAddress}
                        className='flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 transition-colors'
                      >
                        {copied ? (
                          <>
                            <Check size={12} />
                            <span>Copied!</span>
                          </>
                        ) : (
                          <>
                            <Copy size={12} />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>
                    <p className='text-sm font-mono font-semibold text-gray-900 break-all'>
                      {address}
                    </p>
                  </div>
                  <div className='p-2 pb-4'>
                    <NavLink
                      to='/profile'
                      onClick={() => setShowWalletMenu(false)}
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1'
                    >
                      <Users size={16} />
                      <span className='font-medium'>Profile</span>
                    </NavLink>
                    <NavLink
                      to='/my-auctions'
                      onClick={() => setShowWalletMenu(false)}
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1'
                    >
                      <Gavel size={16} />
                      <span className='font-medium'>My Propri Bid</span>
                    </NavLink>
                    <NavLink
                      to='/profile/proprimarket-history'
                      onClick={() => setShowWalletMenu(false)}
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1'
                    >
                      <Handbag size={16} />
                      <span className='font-medium'>My Propri Market</span>
                    </NavLink>
                    <NavLink
                      to='/my-vouchers'
                      onClick={() => setShowWalletMenu(false)}
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1'
                    >
                      <Handbag size={16} />
                      <span className='font-medium'>My Propri Voucher</span>
                    </NavLink>
                    {asetquLandEnabled && (
                      <NavLink
                        to='/my-asetqu-land'
                        onClick={() => setShowWalletMenu(false)}
                        className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1'
                      >
                        <Sprout size={16} />
                        <span className='font-medium'>Lahan Saya</span>
                      </NavLink>
                    )}
                    <NavLink
                      to='/my-aset-academy'
                      onClick={() => setShowWalletMenu(false)}
                      className='w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors mb-1'
                    >
                      <Book size={16} />
                      <span className='font-medium'>My Aset Academy</span>
                    </NavLink>

                    <div className='w-full bg-gray-200 h-[2px] my-2' />
                    <button
                      onClick={handleDisconnect}
                      disabled={logout.isPending}
                      className='w-full flex items-center mt-2 gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
                    >
                      <Wallet size={16} />
                      <span className='font-medium'>
                        {logout.isPending ? 'Disconnecting...' : 'Disconnect Wallet'}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default TopNav;
